import React, { useState } from 'react';
import { Tile, Button, Modal } from '@carbon/react';
import { Add, Wifi, Notification } from '@carbon/icons-react';
import { useTheme } from '../../context/ThemeContext';
import { useAuth } from '../../context/AuthContext';
import { useSubscription } from '../../hooks/useSubscription';
import { useAudUsdFxRate } from '../../hooks/useFxRate';

const UsagePricing = ({ usage }) => {
  const { theme } = useTheme();
  const { user, session, isAuthenticated } = useAuth();
  const { hasActiveSubscription } = useSubscription();
  const { rate: audUsdRate } = useAudUsdFxRate();
  const [subscriptionRequiredModalOpen, setSubscriptionRequiredModalOpen] = useState(false);
  const isDark = theme === 'dark';
  const bgPrimary = isDark ? '#161616' : '#ffffff';
  const bgSecondary = isDark ? '#262626' : '#f4f4f4';
  const textPrimary = isDark ? '#f4f4f4' : '#161616';
  const textSecondary = isDark ? '#c6c6c6' : '#525252';
  const borderColor = isDark ? '#393939' : '#e0e0e0';

  const isAudPrice = typeof usage?.price === 'string' && usage.price.includes('AUD');
  const match = typeof usage?.price === 'string' ? usage.price.match(/\$([0-9]+(?:\.[0-9]+)?)/) : null;
  const audAmount = match ? Number(match[1]) : null;
  const usdApprox =
    isAudPrice &&
    typeof audUsdRate === 'number' &&
    Number.isFinite(audUsdRate) &&
    typeof audAmount === 'number' &&
    Number.isFinite(audAmount)
      ? audAmount * audUsdRate
      : null;

  const handleCTAClick = async () => {
    if (usage.name === 'Additional Sensors') {
      if (!isAuthenticated) {
        window.location.href = '/signup?feature=additional-sensors';
        return;
      }
      if (!hasActiveSubscription) {
        setSubscriptionRequiredModalOpen(true);
        return;
      }
      try {
        const response = await fetch('/.netlify/functions/additional-sensors', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(session?.access_token && { Authorization: `Bearer ${session.access_token}` }),
          },
          body: JSON.stringify({ 
            quantity: 1,
            userId: user.id
          })
        });

        const data = await response.json();
        
        if (response.ok && data.session) {
          window.location.href = data.session.url;
        } else {
          alert('Error purchasing additional sensors: ' + data.error);
        }
      } catch (error) {
        console.error('Error purchasing additional sensors:', error);
        alert('Error purchasing additional sensors. Please try again.');
      }
    } else if (usage.name === 'Cellular Connectivity') {
      if (!isAuthenticated) {
        window.location.href = '/signup?feature=gateway-connectivity';
        return;
      }
      if (!hasActiveSubscription) {
        setSubscriptionRequiredModalOpen(true);
        return;
      }
      try {
        const response = await fetch('/.netlify/functions/gateway-connectivity', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(session?.access_token && { Authorization: `Bearer ${session.access_token}` }),
          },
          body: JSON.stringify({ 
            quantity: 1,
            userId: user.id
          })
        });

        const data = await response.json();
        
        if (response.ok && data.session) {
          window.location.href = data.session.url;
        } else {
          alert('Error purchasing gateway connectivity: ' + data.error);
        }
      } catch (error) {
        console.error('Error purchasing gateway connectivity:', error);
        alert('Error purchasing gateway connectivity. Please try again.');
      }
    }
  };

  const getIcon = (name) => {
    if (name.includes('Sensors')) return Add;
    if (name.includes('Cellular')) return Wifi;
    if (name.includes('SMS')) return Notification;
    return Add;
  };

  const Icon = getIcon(usage.name);

  return (
    <>
      <Tile 
        style={{
          backgroundColor: bgPrimary,
          border: `1px solid ${borderColor}`,
          borderRadius: '12px',
          padding: '32px',
          marginBottom: '24px',
          transition: 'transform 0.2s ease',
          display: 'flex',
          gap: '24px',
          alignItems: 'flex-start'
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.transform = 'translateX(4px)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.transform = 'translateX(0)';
        }}
      >
        {/* Icon */}
        <div style={{
          backgroundColor: isDark ? '#262626' : '#f4f4f4',
          borderRadius: '12px',
          width: '64px',
          height: '64px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0
        }}>
          <Icon size={32} style={{ color: '#0f62fe' }} />
        </div>

        {/* Content */}
        <div style={{ flex: 1 }}>
          <h3 style={{ 
            fontSize: '1.25rem', 
            fontWeight: '600', 
            marginBottom: '8px',
            color: textPrimary
          }}>
            {usage.name}
          </h3>
          
          <div style={{ 
            fontSize: '1.5rem', 
            fontWeight: '700', 
            color: '#0f62fe',
            marginBottom: '12px'
          }}>
            {usage.price}
            {usdApprox !== null && (
              <span style={{ color: textSecondary, fontSize: '0.875rem', fontWeight: 500 }}>
                {' '}
                (
                {new Intl.NumberFormat('en-US', {
                  style: 'currency',
                  currency: 'USD',
                  maximumFractionDigits: usdApprox < 1 ? 2 : 0,
                }).format(usdApprox)}{' '}
                USD)
              </span>
            )}
          </div>

          <p style={{ 
            color: textSecondary, 
            fontSize: '0.95rem', 
            lineHeight: '1.5',
            marginBottom: '16px'
          }}>
            {usage.description}
          </p>

          {usage.example && (
            <div style={{
              backgroundColor: isDark ? '#262626' : '#f4f4f4',
              padding: '12px 16px',
              borderRadius: '8px',
              fontSize: '0.875rem',
              color: textSecondary,
              borderLeft: '3px solid #0f62fe',
              marginBottom: '16px'
            }}>
              <strong>Example:</strong> {usage.example}
            </div>
          )}

          {usage.cta && (
            <Button kind="primary" size="sm" onClick={handleCTAClick} style={{ marginTop: '8px' }}>
              {usage.cta}
            </Button>
          )}
        </div>
      </Tile>

      {/* Modal: render outside Tile so it isn't behind other cards (stacking context) */}
      <Modal
        open={subscriptionRequiredModalOpen}
        modalHeading="Active subscription required"
        primaryButtonText="OK"
        onRequestClose={() => setSubscriptionRequiredModalOpen(false)}
        onRequestSubmit={() => setSubscriptionRequiredModalOpen(false)}
        className="usage-pricing-modal"
      >
        <p style={{ margin: 0 }}>
          You need to have an active subscription first before you add additional sensors or gateway connectivity. Subscribe to a plan from the Plans tab to get started.
        </p>
      </Modal>
    </>
  );
};

export default UsagePricing;
